import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { PublicShell, localPath, usePageLanguage, useTitle } from './PublicPage';
import { CATEGORIES, ESSENTIALS, POSTS, SERIES, findPost, loadBodies, relatedPosts, seriesOf, seriesParts } from './blog/posts';
import { Diagram } from './blog/diagrams';
import { Logo } from './Brand';
import { GuideBox } from './Guide';
import { useArticleTracking } from './analytics';
import { useI18n } from './i18n';

// Turns article text into elements: "**bold**", plus links written as
// "[words](post:slug)" for another article, "[words](tool:slug)" for a calculator or
// "[words](page:slug)" for a site page like /learn.
// Links point to the page in the reader's language.
const INLINE = /(\*\*[^*]+\*\*|\[[^\]]+\]\((?:post|tool|page):[a-z0-9-]+\))/;
function inline(text, lang) {
  return text.split(INLINE).map((part, i) => {
    /* eslint-disable react/no-array-index-key */
    if (part.startsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    const link = part.match(/^\[([^\]]+)\]\((post|tool|page):([a-z0-9-]+)\)$/);
    if (link) {
      // An article that isn't out yet is plain text until its day.
      if (link[2] === 'post' && !findPost(link[3])) return <Fragment key={i}>{link[1]}</Fragment>;
      const path = { post: `/blog/${link[3]}`, tool: `/tools/${link[3]}`, page: `/${link[3]}` }[link[2]];
      return <Link key={i} to={localPath(lang, path)} className="blog-inline-link">{link[1]}</Link>;
    }
    return <Fragment key={i}>{part}</Fragment>;
    /* eslint-enable react/no-array-index-key */
  });
}

// Reading time saved in meta.json (checked against the text by blog.test.js).
function minutesOf(post, lang) {
  return (post.minutes && (post.minutes[lang] || post.minutes.en)) || 1;
}

// The article text, loaded on blog pages only so the rest of the site stays light.
function useBodies() {
  const [bodies, setBodies] = useState(null);
  useEffect(() => {
    let alive = true;
    loadBodies().then((b) => alive && setBodies(b)).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return bodies;
}

function formatDate(date, lang) {
  return new Date(`${date}T12:00:00`).toLocaleDateString(lang === 'es' ? 'es-419' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function blogPath(lang, slug = '') {
  return localPath(lang, `/blog${slug ? `/${slug}` : ''}`);
}

// An article's cover: drawn by the page, so it stays sharp and appears in the reader's language.
export function BlogCover({ post, lang, large = false }) {
  const cover = (post[lang] || post.en).cover || {};
  return (
    <div className={`blog-cover cover-${post.category || 'start'}${large ? ' large' : ''}`} aria-hidden="true">
      <span className="blog-cover-kicker">{cover.kicker}</span>
      <span className="blog-cover-title">{cover.title} <em>{cover.accent}</em></span>
      {cover.sub && <span className="blog-cover-sub">{cover.sub}</span>}
      {cover.pills && <span className="blog-cover-pills">{cover.pills.map((p) => <span key={p}>{p}</span>)}</span>}
      <span className="blog-cover-brand"><Logo /> Market Matrix</span>
    </div>
  );
}

// One article card: cover, category, title, description, date and reading time.
function PostTile({ post, lang }) {
  const { t } = useI18n();
  const text = post[lang] || post.en;
  const category = t((CATEGORIES.find((c) => c.id === post.category) || {}).label || '');
  return (
    <Link to={blogPath(lang, post.slug)} className="blog-tile">
      <BlogCover post={post} lang={lang} />
      <span className="blog-tile-body">
        <span className={`blog-tag tag-${post.category}`}>{category}</span>
        <span className="blog-tile-title">{text.title}</span>
        <span className="blog-tile-text">{text.description}</span>
        <span className="blog-meta">
          {formatDate(post.date, lang)} &middot; {t('{n} min read', { n: minutesOf(post, lang) })}
        </span>
      </span>
    </Link>
  );
}

const PER_PAGE = 9;

export function BlogIndex({ user, forceLang }) {
  // Fetch article text in the background so opening one is instant.
  useEffect(() => {
    loadBodies().catch(() => {});
  }, []);
  const lang = usePageLanguage(forceLang, '/blog');
  const { t } = useI18n();
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [params, setParams] = useSearchParams();
  useTitle(`${t('Blog')} | Market Matrix`);
  const q = query.trim().toLowerCase();
  const labelOf = (id) => t((CATEGORIES.find((c) => c.id === id) || {}).label || '');
  const shown = POSTS.filter((post) => {
    if (category !== 'all' && post.category !== category) return false;
    if (!q) return true;
    const text = post[lang] || post.en;
    return `${text.title} ${text.description} ${labelOf(post.category)}`.toLowerCase().includes(q);
  });
  const used = CATEGORIES.filter((c) => POSTS.some((p) => p.category === c.id));

  // Nine articles per page; the page number lives in the address (?page=2).
  const pages = Math.max(1, Math.ceil(shown.length / PER_PAGE));
  const page = Math.min(pages, Math.max(1, parseInt(params.get('page'), 10) || 1));
  const pagePosts = shown.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  function goToPage(n) {
    setParams(n > 1 ? { page: String(n) } : {});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  // A new filter or search starts again from the first page.
  const filtered = useRef(false);
  useEffect(() => {
    if (filtered.current) setParams({}, { replace: true });
    filtered.current = true;
  }, [category, q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PublicShell user={user} className="blog-page blog-index">
      <div className="page-title">
        <p className="eyebrow">{t('Blog')}</p>
        <h1>{t('Lessons for prop traders')}</h1>
        <p className="subtitle">{t('Practical ideas on risk, routine and passing your evaluations, from the team behind Market Matrix.')}</p>
      </div>
      <Link to={localPath(lang, '/learn')} className="learn-banner">
        <span className="learn-banner-label">{t('New to trading?')}</span>
        <span>{t('Follow the learning path: short series in order, from what trading is to getting better every week.')}</span>
        <span className="learn-banner-go">{t('Start here')} &rarr;</span>
      </Link>
      <div className="blog-toolbar">
        <div className="blog-filters" role="group" aria-label={t('Filter by category')}>
          {[{ id: 'all', label: 'All' }, ...used].map((c) => (
            <button key={c.id} type="button" className={`blog-filter${category === c.id ? ' active' : ''}`} aria-pressed={category === c.id}
              onClick={() => setCategory(c.id)}>
              {t(c.label)}
            </button>
          ))}
        </div>
        <label className="blog-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4.2-4.2" /></svg>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('Search articles…')} aria-label={t('Search articles')} />
        </label>
      </div>
      {shown.length ? (
        <>
          <ul className="blog-grid">
            {pagePosts.map((post) => <li key={post.slug}><PostTile post={post} lang={lang} /></li>)}
          </ul>
          {pages > 1 && (
            <nav className="blog-pages" aria-label={t('Pages')}>
              <button type="button" className="secondary small" onClick={() => goToPage(page - 1)} disabled={page === 1}>&larr; {t('Previous')}</button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                <button key={n} type="button" className={`secondary small blog-page-number${n === page ? ' active' : ''}`}
                  aria-current={n === page ? 'page' : undefined} onClick={() => goToPage(n)}>
                  {n}
                </button>
              ))}
              <button type="button" className="secondary small" onClick={() => goToPage(page + 1)} disabled={page === pages}>{t('Next')} &rarr;</button>
            </nav>
          )}
        </>
      ) : (
        <p className="empty blog-empty">{t('No articles match your search.')}</p>
      )}
    </PublicShell>
  );
}

function Block({ block, ctaTo, lang }) {
  if (block.h2) return <h2>{block.h2}</h2>;
  if (block.p) return <p>{inline(block.p, lang)}</p>;
  if (block.callout) return <blockquote className="blog-callout">{block.callout}</blockquote>;
  if (block.note) return <p className="blog-note">{block.note}</p>;
  if (block.diagram) return <Diagram name={block.diagram} caption={block.caption} lang={lang} />;
  if (block.cta) return <p className="blog-cta"><Link to={ctaTo} className="cta-button">{block.cta} &rarr;</Link></p>;
  if (block.tool) return <p className="blog-cta"><Link to={localPath(lang, `/tools/${block.tool}`)} className="cta-button">{block.label} &rarr;</Link></p>;
  if (block.page) return <p className="blog-cta"><Link to={localPath(lang, `/${block.page}`)} className="cta-button">{block.label} &rarr;</Link></p>;
  if (block.list) {
    return <ul>{block.list.map((item) => <li key={item}>{inline(item, lang)}</li>)}</ul>;
  }
  if (block.table) {
    return (
      <div className="table-wrap blog-table">
        <table>
          <thead><tr>{block.table.head.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
          <tbody>
            {block.table.rows.map((row) => (
              <tr key={row[0]}>{row.map((cell, i) => (i === 0 ? <th key={i} scope="row">{inline(cell, lang)}</th> : <td key={i}>{inline(cell, lang)}</td>))}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

// The list of parts in a series, with buttons to the previous and next article.
function SeriesBox({ series, lang }) {
  const { t } = useI18n();
  const title = (p) => (p[lang] || p.en).title;
  const prev = series.parts[series.index - 1];
  const next = series.parts[series.index + 1];
  return (
    <nav className="blog-series" id="series" aria-label={t('Series')}>
      <h2>{series.title[lang] || series.title.en}</h2>
      <ol>
        {series.parts.map((p, i) => (
          <li key={p.slug} className={i === series.index ? 'is-current' : undefined}>
            {i === series.index
              ? <span aria-current="page">{title(p)}</span>
              : <Link to={localPath(lang, `/blog/${p.slug}`)}>{title(p)}</Link>}
          </li>
        ))}
      </ol>
      <div className="blog-series-nav">
        {prev && (
          <Link to={localPath(lang, `/blog/${prev.slug}`)} className="is-prev">
            <span>&larr; {t('Previous: part {n}', { n: series.index })}</span>{title(prev)}
          </Link>
        )}
        {next && (
          <Link to={localPath(lang, `/blog/${next.slug}`)} className="is-next">
            <span>{t('Next: part {n}', { n: series.index + 2 })} &rarr;</span>{title(next)}
          </Link>
        )}
      </div>
      <p className="blog-series-path"><Link to={localPath(lang, '/learn')}>{t('See the full learning path')} &rarr;</Link></p>
    </nav>
  );
}

export function BlogPost({ user, forceLang }) {
  const { slug } = useParams();
  const lang = usePageLanguage(forceLang, `/blog/${slug}`);
  const { t } = useI18n();
  const post = findPost(slug);
  const text = post ? post[lang] || post.en : null;
  const articleRef = useRef(null);
  const bodies = useBodies();
  useTitle(text ? `${text.title} | Market Matrix` : 'Market Matrix');
  useArticleTracking(post ? post.slug : null, lang, articleRef);
  if (!post) return <Navigate to={blogPath(lang)} replace />;
  const entry = bodies && bodies[post.slug];
  const body = entry ? entry[lang] || entry.en : null;
  const ctaTo = user ? '/app' : '/signup';
  const series = seriesOf(post.slug);

  return (
    <PublicShell user={user} className="blog-page">
      <article className="blog-article" ref={articleRef}>
        <Link to={blogPath(lang)} className="blog-back">&larr; {t('All articles')}</Link>
        <BlogCover post={post} lang={lang} large />
        <p className="blog-meta">
          {formatDate(post.date, lang)} &middot; {t('{n} min read', { n: minutesOf(post, lang) })}
        </p>
        {series && (
          <p className="blog-series-tag">
            <a href="#series">{t('Part {n} of {total}', { n: series.index + 1, total: series.parts.length })} &middot; {series.title[lang] || series.title.en}</a>
          </p>
        )}
        <h1>{text.title}</h1>
        <p className="blog-lede">{text.description}</p>
        {body ? body.map((block, i) => (
          // Blocks never move, so their position is a stable key.
          // eslint-disable-next-line react/no-array-index-key
          <Block key={i} block={block} ctaTo={ctaTo} lang={lang} />
        )) : <div className="blog-loading" aria-busy="true"><span /><span /><span /></div>}
        {series && <SeriesBox series={series} lang={lang} />}
      </article>
      {!user && <GuideBox source={`Article: ${post.slug}`} />}
      <section className="blog-related" aria-labelledby="keep-reading">
        <h2 id="keep-reading">{t('Keep reading')}</h2>
        <ul className="blog-grid">
          {relatedPosts(post.slug).map((p) => <li key={p.slug}><PostTile post={p} lang={lang} /></li>)}
        </ul>
      </section>
      <section className="final-cta blog-final-cta">
        <h2>{user ? t('Journal your next session') : t('Journal your next session free')}</h2>
        <p>{t('Put this into practice: import your trades and Market Matrix tracks your prop firm rules, your routine and what’s really making you money.')}</p>
        <Link to={ctaTo} className="cta-button">{user ? t('Open journal') : t('Start your free journal')} &rarr;</Link>
        {!user && <span className="cta-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</span>}
      </section>
    </PublicShell>
  );
}

// The learning path: every series in order, then the prop firm essentials.
export function LearnPage({ user, forceLang }) {
  // Fetch article text in the background so opening one is instant.
  useEffect(() => {
    loadBodies().catch(() => {});
  }, []);
  const lang = usePageLanguage(forceLang, '/learn');
  const { t } = useI18n();
  useTitle(t('Learn to trade, step by step | Market Matrix'));
  const title = (p) => (p[lang] || p.en).title;
  // A series shows once its first part is live.
  const steps = Object.entries(SERIES).map(([id, info]) => ({ id, info, parts: seriesParts(id) })).filter((s) => s.parts.length);
  const essentials = ESSENTIALS.map(findPost).filter(Boolean);
  return (
    <PublicShell user={user} className="blog-page learn-page">
      <div className="page-title">
        <p className="eyebrow">{t('Start here')}</p>
        <h1>{t('Learn to trade, step by step')}</h1>
        <p className="subtitle">{t('Free, honest lessons for new traders, in the order we’d read them. Each series takes about an hour, and every article ends with how to test the idea in your own journal.')}</p>
      </div>
      <ol className="learn-steps">
        {steps.map(({ id, info, parts }, i) => (
          <li key={id} className="learn-step">
            <span className="learn-step-number">{String(i + 1).padStart(2, '0')}</span>
            <div className="learn-step-body">
              <h2>{info[lang] || info.en}</h2>
              <p className="learn-step-blurb">{info.blurb[lang] || info.blurb.en} &middot; {t('{n} parts', { n: parts.length })}</p>
              <ol className="learn-parts">
                {parts.map((p) => <li key={p.slug}><Link to={localPath(lang, `/blog/${p.slug}`)}>{title(p)}</Link></li>)}
              </ol>
              {parts[0] && <Link to={localPath(lang, `/blog/${parts[0].slug}`)} className="cta-button learn-start">{t('Start part 1')} &rarr;</Link>}
            </div>
          </li>
        ))}
        <li className="learn-step">
          <span className="learn-step-number">{String(steps.length + 1).padStart(2, '0')}</span>
          <div className="learn-step-body">
            <h2>{t('Prop firm essentials')}</h2>
            <p className="learn-step-blurb">{t('Ready for an evaluation? The rules that decide whether you pass and get paid.')}</p>
            <ul className="learn-parts">
              {essentials.map((p) => <li key={p.slug}><Link to={localPath(lang, `/blog/${p.slug}`)}>{title(p)}</Link></li>)}
            </ul>
          </div>
        </li>
      </ol>
      <section className="final-cta blog-final-cta">
        <h2>{t('Practice what you learn')}</h2>
        <p>{t('Market Matrix tracks your prop firm rules, your routine and what’s really making you money, from your own trades.')}</p>
        <Link to={user ? '/app' : '/signup'} className="cta-button">{user ? t('Open journal') : t('Start your free journal')} &rarr;</Link>
        {!user && <span className="cta-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</span>}
      </section>
    </PublicShell>
  );
}
